// Wave 20.49at — Alerts inbox page.
//
// Route: /alerts. Shows the latest 200 alerts newest-first.
// Rows link to the underlying drop when applicable (reaction /
// comment / drop_sent), otherwise to the actor's profile
// (follow). On mount we mark everything unread as seen so the
// bell badge clears.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PageLayout from './components/PageLayout';
import { colors } from './theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

interface Alert {
  id: string;
  kind:
    | 'drop_reaction'
    | 'drop_comment'
    | 'drop_sent'
    | 'follow'
    | 'live'
    | string;
  actor: { id: number | null; displayName: string | null };
  dropType: 'activity' | 'peak' | null;
  dropToken: string | null;
  dropUrl: string | null;
  emoji: string | null;
  preview: string | null;
  seenAt: string | null;
  createdAt: string;
}

async function authFetch(path: string, init?: RequestInit): Promise<Response> {
  const jwt = localStorage.getItem(TOKEN_KEY);
  return fetch(`${API}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
  });
}

function fmtRel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 30 * 86_400_000) return `${Math.floor(ms / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function kindHeadline(a: Alert): string {
  const who = a.actor.displayName || 'Someone';
  switch (a.kind) {
    case 'drop_reaction':
      return `${who} reacted to your drop`;
    case 'drop_comment':
      return `${who} commented on your drop`;
    case 'drop_sent':
      return `${who} sent you a drop`;
    case 'follow':
      return `${who} started following you`;
    case 'live':
      return `${who} is live now`;
    default:
      return `${who} · ${a.kind}`;
  }
}

export default function AlertsPage() {
  const [items, setItems] = useState<Alert[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch('/alerts');
      if (!res.ok) throw new Error(String(res.status));
      const rows = (await res.json()) as Alert[];
      setItems(rows);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  }, []);

  useEffect(() => {
    void load();
    // Mark all-seen on mount so the badge clears. Non-blocking
    // — even if the PATCH fails, list rendering proceeds.
    void authFetch('/alerts/read-all', { method: 'PATCH' }).catch(() => {});
  }, [load]);

  return (
    <PageLayout maxWidth="sm">
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsIcon sx={{ color: colors.brand }} />
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Alerts
          </Typography>
        </Box>

        {error && (
          <Typography sx={{ color: colors.danger }}>{error}</Typography>
        )}

        {items === null && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: colors.brand }} />
          </Box>
        )}

        {items && items.length === 0 && (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.surface,
            }}
          >
            <Typography sx={{ color: colors.textMuted }}>
              You're all caught up. Reactions, comments, drops, and follows
              will land here.
            </Typography>
          </Box>
        )}

        {items && items.length > 0 && (
          <Stack spacing={1}>
            {items.map((a) => {
              const href =
                a.dropUrl
                  ? a.dropUrl
                  : a.kind === 'follow' && a.actor.id
                  ? `/u/${a.actor.id}`
                  : null;
              return (
                <Box
                  key={a.id}
                  component={href ? 'a' : 'div'}
                  {...(href ? { href } : {})}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.surface,
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'border-color 120ms',
                    '&:hover': href ? { borderColor: colors.brand } : {},
                    opacity: a.seenAt ? 0.7 : 1,
                  }}
                >
                  {/* Unread dot */}
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: a.seenAt ? 'transparent' : colors.brand,
                      mt: 1,
                      flex: '0 0 auto',
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ color: colors.text, fontWeight: 700, fontSize: '0.9rem' }}
                    >
                      {kindHeadline(a)}
                    </Typography>
                    {a.preview && (
                      <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.25 }}>
                        {a.preview}
                      </Typography>
                    )}
                    <Typography sx={{ color: colors.textFaint, fontSize: '0.72rem', mt: 0.5 }}>
                      {fmtRel(a.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    </PageLayout>
  );
}
